import { memberFetch } from './memberApi';

export const MEMBER_NAVIGATION_ORDER = ['progress', 'resources', 'learning', 'billing'];
export const DEFAULT_MEMBER_PREFERENCES = Object.freeze({
  appearance: 'system',
  display_size: 'default',
  navigation_order: Object.freeze([...MEMBER_NAVIGATION_ORDER]),
  navigation_hidden: Object.freeze([]),
});

export const MEMBER_DISPLAY_SIZE_OPTIONS = Object.freeze([
  { value: 'compact', label: 'Compact', scale: '95%' },
  { value: 'default', label: 'Default', scale: '100%' },
  { value: 'comfortable', label: 'Comfortable', scale: '110%' },
  { value: 'large', label: 'Large', scale: '120%' },
  { value: 'largest', label: 'Largest', scale: '130%' },
]);

const APPEARANCES = new Set(['system', 'light', 'dark']);
const DISPLAY_SIZES = new Set(MEMBER_DISPLAY_SIZE_OPTIONS.map(({ value }) => value));
const NAVIGATION_TABS = new Set(MEMBER_NAVIGATION_ORDER);
const CACHE_PREFIX = 'memberPreferences';

export const normalizeMemberPreferences = (preferences = {}) => {
  const source = preferences && typeof preferences === 'object' && !Array.isArray(preferences)
    ? preferences
    : {};
  const requestedOrder = Array.isArray(source.navigation_order)
    ? source.navigation_order
    : [];
  const navigationOrder = requestedOrder.filter((id, index) => (
    NAVIGATION_TABS.has(id) && requestedOrder.indexOf(id) === index
  ));
  const requestedHidden = Array.isArray(source.navigation_hidden)
    ? source.navigation_hidden
    : [];
  return {
    appearance: APPEARANCES.has(source.appearance)
      ? source.appearance
      : DEFAULT_MEMBER_PREFERENCES.appearance,
    display_size: DISPLAY_SIZES.has(source.display_size)
      ? source.display_size
      : DEFAULT_MEMBER_PREFERENCES.display_size,
    navigation_order: [
      ...navigationOrder,
      ...MEMBER_NAVIGATION_ORDER.filter((id) => !navigationOrder.includes(id)),
    ],
    navigation_hidden: requestedHidden.filter((id, index) => (
      id !== 'progress' && NAVIGATION_TABS.has(id) && requestedHidden.indexOf(id) === index
    )),
  };
};

export const getMemberPreferencesCacheKey = ({ memberId, authType } = {}) => {
  if (!memberId) return null;
  return `${CACHE_PREFIX}:${authType === 'student' ? 'student' : 'customer'}:${memberId}`;
};

export const getMemberPortalStorageKey = (name, identity = {}) => {
  const memberKey = getMemberPreferencesCacheKey(identity);
  return memberKey && name ? `${memberKey}:${name}` : null;
};

export const getCachedMemberPreferences = (cacheKey) => {
  if (!cacheKey || typeof localStorage === 'undefined') {
    return normalizeMemberPreferences();
  }
  try {
    return normalizeMemberPreferences(JSON.parse(localStorage.getItem(cacheKey) || '{}'));
  } catch {
    return normalizeMemberPreferences();
  }
};

export const cacheMemberPreferences = (cacheKey, preferences) => {
  const normalized = normalizeMemberPreferences(preferences);
  if (cacheKey && typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(normalized));
    } catch {
      // Storage can be unavailable in private/restricted browser contexts.
      // Applying server-backed preferences must not depend on the PWA cache.
    }
  }
  return normalized;
};

const readJson = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.detail || fallback);
  return body;
};

export const getMemberPreferences = async () => readJson(
  await memberFetch('/api/customer/preferences'),
  'Unable to load your preferences.',
);

export const updateMemberPreferences = async (preferences) => readJson(
  await memberFetch('/api/customer/preferences', {
    method: 'PUT',
    body: JSON.stringify(normalizeMemberPreferences(preferences)),
  }),
  'Unable to save your preferences.',
);

export const updateMemberAppearance = async (appearance) => readJson(
  await memberFetch('/api/customer/preferences', {
    method: 'PUT',
    body: JSON.stringify({
      appearance: APPEARANCES.has(appearance)
        ? appearance
        : DEFAULT_MEMBER_PREFERENCES.appearance,
    }),
  }),
  'Unable to save your appearance.',
);

export const applyMemberDisplaySize = (displaySize) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.memberDisplaySize = DISPLAY_SIZES.has(displaySize)
    ? displaySize
    : 'default';
};
